import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import './Admin.css';
import './Admin.css';

// ─── Helpers ─────────────────────────────────────────────────
const Money     = (n) => `Rs ${Number(n || 0).toLocaleString()}`;
const toISO     = (d) => d.toISOString().split('T')[0];
const isSameDay = (d1, d2) =>
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth()    === d2.getMonth()    &&
  d1.getDate()     === d2.getDate();

const isInRange = (date, from, to) => {
  if (!date) return false;
  const d = date.getTime();
  const f = from ? new Date(from).getTime()            : null;
  const t = to   ? new Date(to + 'T23:59:59').getTime(): null;
  if (f && d < f) return false;
  if (t && d > t) return false;
  return true;
};

const getCreatedDate   = (o) => { const v = o?.created_at  || o?.createdAt;   const d = v ? new Date(v) : null; return d && !isNaN(d) ? d : null; };
const getDeliveredDate = (o) => { const v = o?.delivered_at || o?.deliveredAt; const d = v ? new Date(v) : null; return d && !isNaN(d) ? d : null; };
const getAmount        = (o) => Number(o?.total_amount ?? o?.totalAmount ?? 0) || 0;

const today      = new Date();
const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

// ─── Component ───────────────────────────────────────────────
export default function AdminDashboard() {
  const { api }  = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [orders,     setOrders]     = useState([]);
  const [products,   setProducts]   = useState([]);
  const [riders,     setRiders]     = useState([]);

  // Date filter state
  const [fromDate,       setFromDate]       = useState(toISO(monthStart));
  const [toDate,         setToDate]         = useState(toISO(today));
  const [appliedFrom,    setAppliedFrom]    = useState(toISO(monthStart));
  const [appliedTo,      setAppliedTo]      = useState(toISO(today));
  const [filterApplied,  setFilterApplied]  = useState(false);

  // ─── Fetch ──────────────────────────────────────────────
  const fetchDashboardData = useCallback(async () => {
    try {
      setError('');
      const [ordersResult, productsResult, ridersResult] = await Promise.allSettled([
        api.get('/orders',   { timeout: 8000 }),
        api.get('/products', { timeout: 8000 }),
        api.get('/riders',   { timeout: 8000 }),
      ]);

      if (ordersResult.status === 'fulfilled') {
        const d = ordersResult.value?.data;
        setOrders(Array.isArray(d) ? d : (d?.orders || d?.data || []));
      } else setOrders([]);

      if (productsResult.status === 'fulfilled') {
        const d = productsResult.value?.data;
        setProducts(Array.isArray(d) ? d : (d?.products || d?.data || []));
      } else setProducts([]);

      if (ridersResult.status === 'fulfilled') {
        const d = ridersResult.value?.data;
        setRiders(Array.isArray(d) ? d : (d?.riders || d?.data || []));
      } else setRiders([]);

      const allFailed = [ordersResult, productsResult, ridersResult].every(r => r.status === 'rejected');
      if (allFailed) setError('Failed to load dashboard data. Please try again.');

    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api]);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

  const handleRefresh = () => { setRefreshing(true); fetchDashboardData(); };

  // ─── Date Filter ────────────────────────────────────────
  const handleApplyFilter = () => {
    setAppliedFrom(fromDate);
    setAppliedTo(toDate);
    setFilterApplied(true);
  };

  const handleResetFilter = () => {
    const f = toISO(monthStart), t = toISO(today);
    setFromDate(f); setToDate(t);
    setAppliedFrom(f); setAppliedTo(t);
    setFilterApplied(false);
  };

  // ─── Metrics ────────────────────────────────────────────
  const metrics = useMemo(() => {
    const all  = Array.isArray(orders) ? orders : [];

    // Filter by date range
    const list = all.filter(o => {
      const d = getCreatedDate(o);
      return isInRange(d, appliedFrom, appliedTo);
    });

    const totalOrders    = list.length;
    const pending        = list.filter(o => o.status?.toLowerCase() === 'pending').length;
    const accepted       = list.filter(o => o.status?.toLowerCase() === 'accepted').length;
    const assigned       = list.filter(o => o.status?.toLowerCase() === 'assigned').length;
    const confirmed      = list.filter(o => o.status?.toLowerCase() === 'confirmed').length;
    const processing     = list.filter(o => o.status?.toLowerCase() === 'processing').length;
    const outForDelivery = list.filter(o => o.status?.toLowerCase() === 'out-for-delivery').length;
    const delivered      = list.filter(o => o.status?.toLowerCase() === 'delivered').length;
    const cancelled      = list.filter(o => o.status?.toLowerCase() === 'cancelled').length;
    const active         = list.filter(o =>
      ['accepted','assigned','confirmed','processing','out-for-delivery'].includes(o.status?.toLowerCase())
    ).length;

    const totalRevenue     = list.reduce((s, o) => s + getAmount(o), 0);
    const deliveredRevenue = list.filter(o => o.status?.toLowerCase() === 'delivered').reduce((s, o) => s + getAmount(o), 0);

    const deliveredTodayCount = list.filter(o => {
      if (o.status?.toLowerCase() !== 'delivered') return false;
      const d = getDeliveredDate(o) || getCreatedDate(o);
      return d ? isSameDay(d, today) : false;
    }).length;

    const deliveredTodayRevenue = list
      .filter(o => o.status?.toLowerCase() === 'delivered')
      .filter(o => { const d = getDeliveredDate(o) || getCreatedDate(o); return d ? isSameDay(d, today) : false; })
      .reduce((s, o) => s + getAmount(o), 0);

    const totalProducts = Array.isArray(products) ? products.length : 0;
    const lowStock      = (Array.isArray(products) ? products : []).filter(p => Number(p?.stock || 0) <= 5).length;
    const onSale        = (Array.isArray(products) ? products : []).filter(p => parseFloat(p?.discount_percent) > 0).length;

    const totalRiders    = Array.isArray(riders) ? riders.length : 0;
    const activeRiderIds = new Set(
      list.filter(o => ['accepted','assigned','confirmed','processing','out-for-delivery'].includes(o.status?.toLowerCase()))
          .map(o => o.rider_id).filter(Boolean)
    );
    const activeRiders = activeRiderIds.size;
    const idleRiders   = Math.max(totalRiders - activeRiders, 0);

    const statusData = [
      { label: 'Pending',    value: pending,        color: '#f6ad55', bg: '#fffbeb' },
      { label: 'Active',     value: active,         color: '#4299e1', bg: '#ebf8ff' },
      { label: 'Delivered',  value: delivered,      color: '#48bb78', bg: '#f0fff4' },
      { label: 'Cancelled',  value: cancelled,      color: '#fc8181', bg: '#fff5f5' },
    ];

    return {
      totalOrders, pending, active, accepted, assigned, confirmed,
      processing, outForDelivery, delivered, cancelled,
      totalRevenue, deliveredRevenue,
      deliveredTodayCount, deliveredTodayRevenue,
      totalProducts, lowStock, onSale,
      totalRiders, activeRiders, idleRiders,
      statusData,
    };
  }, [orders, products, riders, appliedFrom, appliedTo]);

  if (loading) return <LoadingSpinner message="Loading dashboard..." />;

  return (
    <div className="admin-dashboard">
      <div className="db-container">

        {/* ══ TOP HEADER ══════════════════════════════════ */}
        <div className="db-top-header">
          <div>
            <p className="db-greeting">👋 Welcome back</p>
            <h1 className="db-header-title">Admin Dashboard</h1>
          </div>
          <button
            className="db-refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
          </button>
        </div>

        {error && (
          <div className="alert alert-error" style={{ margin: '0 0 20px' }}>
            <span className="alert-icon">⚠️</span>
            <span className="alert-text">{error}</span>
          </div>
        )}

        {/* ══ DATE FILTER ══════════════════════════════════ */}
        <div className="db-filter-card">
          <div className="db-filter-title-row">
            <span className="db-filter-title">🗓️ Date Range Filter</span>
            {filterApplied && (
              <span className="db-filter-active-pill">● Filter Active</span>
            )}
          </div>
          <p className="db-filter-hint">Filter dashboard data by order creation date</p>

          <div className="db-filter-row">
            <div className="db-filter-field">
              <label className="db-filter-label">From Date</label>
              <input
                type="date"
                className="db-filter-input"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
              />
            </div>
            <span className="db-filter-arrow">→</span>
            <div className="db-filter-field">
              <label className="db-filter-label">To Date</label>
              <input
                type="date"
                className="db-filter-input"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
              />
            </div>
          </div>

          <div className="db-filter-btns">
            <button className="db-apply-btn" onClick={handleApplyFilter}>
              🔍 Apply Filter
            </button>
            <button className="db-reset-btn" onClick={handleResetFilter}>
              ✕ Reset
            </button>
          </div>

          {filterApplied && (
            <p className="db-filter-active-text">
              📅 Showing data: {appliedFrom} → {appliedTo}
            </p>
          )}
        </div>

        {/* ══ REVENUE HERO CARDS ═══════════════════════════ */}
        <div className="db-section-header">
          <span className="db-section-icon">💰</span>
          <h2 className="db-section-title">Revenue</h2>
        </div>

        <div className="db-hero-grid">
          <div className="db-hero-card" style={{ borderLeftColor: '#2d6a4f' }}>
            <span className="db-hero-icon">💵</span>
            <div>
              <div className="db-hero-value" style={{ color: '#2d6a4f' }}>{Money(metrics.totalRevenue)}</div>
              <div className="db-hero-label">Total Revenue (period)</div>
              <div className="db-hero-sub">
                Delivered: <strong style={{ color: '#2d6a4f' }}>{Money(metrics.deliveredRevenue)}</strong>
              </div>
            </div>
          </div>
          <div className="db-hero-card" style={{ borderLeftColor: '#3182ce' }}>
            <span className="db-hero-icon">📅</span>
            <div>
              <div className="db-hero-value" style={{ color: '#3182ce' }}>{metrics.deliveredTodayCount} orders</div>
              <div className="db-hero-label">Delivered Today</div>
              <div className="db-hero-sub">
                Revenue: <strong style={{ color: '#3182ce' }}>{Money(metrics.deliveredTodayRevenue)}</strong>
              </div>
            </div>
          </div>
        </div>

        {/* ══ ORDERS OVERVIEW ══════════════════════════════ */}
        <div className="db-section-header">
          <span className="db-section-icon">📦</span>
          <h2 className="db-section-title">Orders Overview</h2>
        </div>

        {/* Total orders big card + legend */}
        <div className="db-total-order-card">
          <div className="db-total-left">
            <div className="db-total-value">{metrics.totalOrders}</div>
            <div className="db-total-label">Total Orders</div>
            <div className="db-total-sub">in selected period</div>
          </div>
          <div className="db-total-right">
            {[
              { color: '#48bb78', label: 'Delivered', value: metrics.delivered  },
              { color: '#f6ad55', label: 'Pending',   value: metrics.pending    },
              { color: '#4299e1', label: 'Active',    value: metrics.active     },
              { color: '#fc8181', label: 'Cancelled', value: metrics.cancelled  },
            ].map(item => (
              <div key={item.label} className="db-dot-row">
                <span className="db-dot" style={{ background: item.color }} />
                <span className="db-dot-label">{item.label}: <strong>{item.value}</strong></span>
              </div>
            ))}
          </div>
        </div>

        {/* Bar chart */}
        <div className="db-chart-card">
          <div className="db-chart-title">Order Status Distribution</div>
          {metrics.statusData.map(s => {
            const pct = metrics.totalOrders > 0 ? (s.value / metrics.totalOrders) * 100 : 0;
            return (
              <div key={s.label} className="db-bar-row">
                <span className="db-bar-label">{s.label}</span>
                <div className="db-bar-track">
                  <div
                    className="db-bar-fill"
                    style={{ width: `${pct}%`, backgroundColor: s.color }}
                  />
                </div>
                <span className="db-bar-value" style={{ color: s.color }}>{s.value}</span>
              </div>
            );
          })}
        </div>

        {/* Detail mini cards */}
        <div className="db-mini-grid">
          {[
            { icon: '📌', value: metrics.assigned,       label: 'Assigned',         color: '#6b46c1', bg: '#faf5ff' },
            { icon: '👍', value: metrics.accepted,       label: 'Accepted',         color: '#2b6cb0', bg: '#ebf8ff' },
            { icon: '⚙️', value: metrics.processing,     label: 'Processing',       color: '#b7791f', bg: '#fffaf0' },
            { icon: '✅', value: metrics.confirmed,      label: 'Confirmed',        color: '#276749', bg: '#f0fff4' },
            { icon: '🚚', value: metrics.outForDelivery, label: 'Out for Delivery', color: '#2c7a7b', bg: '#e6fffa' },
            { icon: '❌', value: metrics.cancelled,      label: 'Cancelled',        color: '#c53030', bg: '#fff5f5' },
          ].map(c => (
            <div key={c.label} className="db-mini-card" style={{ backgroundColor: c.bg }}>
              <span className="db-mini-icon">{c.icon}</span>
              <span className="db-mini-value" style={{ color: c.color }}>{c.value}</span>
              <span className="db-mini-label">{c.label}</span>
            </div>
          ))}
        </div>

        {/* ══ PRODUCTS ═════════════════════════════════════ */}
        <div className="db-section-header">
          <span className="db-section-icon">🥬</span>
          <h2 className="db-section-title">Products</h2>
        </div>

        <div className="db-mini-grid">
          <div className="db-mini-card" style={{ backgroundColor: '#f0fff4' }}>
            <span className="db-mini-icon">📦</span>
            <span className="db-mini-value" style={{ color: '#2d6a4f' }}>{metrics.totalProducts}</span>
            <span className="db-mini-label">Total Products</span>
          </div>
          <div className="db-mini-card" style={{ backgroundColor: '#fff5e6' }}>
            <span className="db-mini-icon">⚠️</span>
            <span className="db-mini-value" style={{ color: '#c05621' }}>{metrics.lowStock}</span>
            <span className="db-mini-label">Low Stock (≤5)</span>
          </div>
          <div className="db-mini-card" style={{ backgroundColor: '#fff0f0' }}>
            <span className="db-mini-icon">🏷️</span>
            <span className="db-mini-value" style={{ color: '#e53e3e' }}>{metrics.onSale}</span>
            <span className="db-mini-label">On Sale</span>
          </div>
        </div>

        {/* ══ RIDERS ═══════════════════════════════════════ */}
        <div className="db-section-header">
          <span className="db-section-icon">🏍️</span>
          <h2 className="db-section-title">Riders</h2>
        </div>

        <div className="db-riders-row">
          <div className="db-rider-big-card" style={{ borderColor: '#2d6a4f' }}>
            <span className="db-rider-icon">🏍️</span>
            <span className="db-rider-value" style={{ color: '#2d6a4f' }}>{metrics.totalRiders}</span>
            <span className="db-rider-label">Total Riders</span>
          </div>
          <div className="db-rider-big-card" style={{ borderColor: '#e53e3e' }}>
            <span className="db-rider-icon">🔥</span>
            <span className="db-rider-value" style={{ color: '#e53e3e' }}>{metrics.activeRiders}</span>
            <span className="db-rider-label">Active</span>
          </div>
          <div className="db-rider-big-card" style={{ borderColor: '#3182ce' }}>
            <span className="db-rider-icon">🧊</span>
            <span className="db-rider-value" style={{ color: '#3182ce' }}>{metrics.idleRiders}</span>
            <span className="db-rider-label">Idle</span>
          </div>
        </div>

        {/* ══ QUICK ACTIONS ════════════════════════════════ */}
        <div className="db-section-header">
          <span className="db-section-icon">⚡</span>
          <h2 className="db-section-title">Quick Actions</h2>
        </div>

        <div className="db-actions-grid">
          {[
            { icon: '🥬', label: 'Manage Products', color: '#276749', path: '/admin/products'     },
            { icon: '📦', label: 'View Orders',      color: '#2b6cb0', path: '/admin/orders'       },
            { icon: '🏍️', label: 'Manage Riders',    color: '#6b46c1', path: '/admin/riders'       },
            { icon: '💰', label: 'Rider Earnings',   color: '#b7791f', path: '/admin/rider-earnings'},
            { icon: '➕', label: 'Add Product',      color: '#2d6a4f', path: '/admin/products/add' },
          ].map(a => (
            <button
              key={a.label}
              className="db-action-btn"
              style={{ backgroundColor: a.color }}
              onClick={() => navigate(a.path)}
            >
              <span className="db-action-icon">{a.icon}</span>
              <span className="db-action-label">{a.label}</span>
            </button>
          ))}
        </div>

      </div>
    </div>
  );
}